import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import {
    Trophy, Clock, MapPin, RefreshCw, Zap, Radio, ChevronRight,
    Activity, Users, Video, Tv, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LiveScoresPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [liveMatches, setLiveMatches] = useState([]);
    const [liveStreams, setLiveStreams] = useState([]);
    const [tournaments, setTournaments] = useState([]);
    const [selectedTournament, setSelectedTournament] = useState('all');
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchLiveMatches = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/api/live-scoring/all-live`);
            setLiveMatches(res.data || []);
            setLastUpdated(new Date());
        } catch (error) {
            console.error('Error fetching live matches:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchLiveStreams = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/api/streaming/live-now`);
            setLiveStreams(res.data || []);
        } catch (error) {
            console.error('Error fetching live streams:', error);
        }
    }, []);

    const fetchTournaments = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/tournaments`);
            setTournaments(res.data || []);
        } catch (error) {
            console.error('Error fetching tournaments:', error);
        }
    };

    useEffect(() => {
        fetchTournaments();
        fetchLiveMatches();
        fetchLiveStreams();
    }, [fetchLiveMatches, fetchLiveStreams]);

    // Auto-refresh every 10 seconds - ALWAYS ENABLED by default
    useEffect(() => {
        if (!autoRefresh) return;
        fetchLiveMatches();
        fetchLiveStreams();
        const interval = setInterval(() => {
            fetchLiveMatches();
            fetchLiveStreams();
        }, 10000);
        return () => clearInterval(interval);
    }, [autoRefresh, fetchLiveMatches, fetchLiveStreams]);

    // Build map of match_id → stream state for quick badge lookup
    const streamingMatchIds = new Set(liveStreams.map(s => s.match_id));

    const filteredMatches = selectedTournament === 'all'
        ? liveMatches
        : liveMatches.filter(m => m.tournament_id === selectedTournament);

    const filteredStreams = selectedTournament === 'all'
        ? liveStreams
        : liveStreams.filter(s => s.tournament?.id === selectedTournament);

    // Group matches by tournament
    const matchesByTournament = filteredMatches.reduce((acc, match) => {
        const tournamentId = match.tournament_id;
        if (!acc[tournamentId]) {
            acc[tournamentId] = {
                name: match.tournament_name,
                sport: match.sport,
                matches: []
            };
        }
        acc[tournamentId].matches.push(match);
        return acc;
    }, {});

    return (
        <div className="min-h-screen bg-[#0a1628]">
            {/* Hero Header */}
            <div className="bg-gradient-to-r from-red-600 to-orange-500 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="flex items-center gap-3 mb-2">
                                <span className="w-4 h-4 bg-white rounded-full animate-pulse" />
                                <span className="text-white/80 uppercase tracking-widest text-sm font-bold">
                                    Live Now
                                </span>
                            </div>
                            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white uppercase tracking-tight">
                                Live Scores & Streaming
                            </h1>
                            <p className="text-white/80 mt-2">
                                Real-time scores and live video from ongoing matches
                            </p>
                        </div>
                        <div className="hidden md:flex items-center gap-4">
                            <div className="text-right">
                                <div className="text-white/60 text-sm">Live Streams</div>
                                <div className="text-4xl font-bold text-white" data-testid="hero-live-stream-count">{liveStreams.length}</div>
                            </div>
                            <Tv className="w-12 h-12 text-white/30" />
                            <div className="text-right ml-4">
                                <div className="text-white/60 text-sm">Live Matches</div>
                                <div className="text-4xl font-bold text-white">{liveMatches.length}</div>
                            </div>
                            <Activity className="w-12 h-12 text-white/30" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Controls */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <Select value={selectedTournament} onValueChange={setSelectedTournament}>
                            <SelectTrigger className="w-[250px] bg-[#0d2847] border-white/10">
                                <SelectValue placeholder="Filter by Tournament" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0d2847] border-white/10">
                                <SelectItem value="all">All Tournaments</SelectItem>
                                {tournaments.map(t => (
                                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex items-center gap-4">
                        {lastUpdated && (
                            <span className="text-sm text-muted-foreground">
                                Last updated: {lastUpdated.toLocaleTimeString()}
                            </span>
                        )}
                        <Button
                            variant={autoRefresh ? "default" : "outline"}
                            size="sm"
                            onClick={() => setAutoRefresh(!autoRefresh)}
                            className={autoRefresh ? "bg-green-500 hover:bg-green-400" : "border-white/10"}
                        >
                            <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
                            {autoRefresh ? 'Live Updates ON' : 'Enable Live Updates'}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={fetchLiveMatches}
                            className="border-white/10"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Live Streams Rail — tournament-attached video streams */}
            {filteredStreams.length > 0 && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-2" data-testid="live-streams-rail">
                    <div className="flex items-center gap-3 mb-3">
                        <Tv className="w-5 h-5 text-red-400" />
                        <h2 className="text-lg font-bold uppercase tracking-wide">Live Streams</h2>
                        <span className="text-sm text-red-400 flex items-center gap-1">
                            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                            {filteredStreams.length} broadcasting
                        </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredStreams.map(s => (
                            <Link key={s.match_id} to={`/live-watch/${s.match_id}`} data-testid={`stream-tile-${s.match_id}`}>
                                <div className="group relative aspect-video bg-gradient-to-br from-red-600/20 to-orange-500/10 border border-red-500/30 rounded-lg overflow-hidden hover:border-red-400 transition-all">
                                    <div className="absolute inset-0 flex flex-col justify-between p-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs px-2 py-0.5 bg-red-500 text-white rounded-full font-bold flex items-center gap-1">
                                                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> LIVE
                                            </span>
                                            {s.stream?.viewer_count_webrtc > 0 && (
                                                <span className="text-xs flex items-center gap-1 text-white">
                                                    <Eye className="w-3 h-3" /> {s.stream.viewer_count_webrtc}
                                                </span>
                                            )}
                                        </div>
                                        <div>
                                            <div className="text-xs text-white/60 mb-1 flex items-center gap-1">
                                                <Trophy className="w-3 h-3" />{s.tournament?.name || 'Match'}
                                            </div>
                                            <div className="font-bold text-white truncate">
                                                {s.home_team || 'Home'} <span className="text-white/50">vs</span> {s.away_team || 'Away'}
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-white/80 mt-1">
                                                <span className="font-bold text-cyan-400">{s.score1}</span>
                                                <span className="text-white/40">-</span>
                                                <span className="font-bold text-purple-400">{s.score2}</span>
                                                {s.venue && <span className="ml-2 text-xs text-white/50 truncate">{s.venue}</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                        <Video className="w-12 h-12 text-white/0 group-hover:text-white/70 transition-all" />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* Live Matches */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="text-center">
                            <RefreshCw className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
                            <div className="text-muted-foreground">Loading live matches...</div>
                        </div>
                    </div>
                ) : filteredMatches.length === 0 ? (
                    <div className="text-center py-20">
                        <Radio className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-xl font-bold mb-2">No Live Matches</h3>
                        <p className="text-muted-foreground mb-6">
                            There are no matches currently in progress.
                        </p>
                        <Button 
                            onClick={() => navigate('/tournaments')}
                            className="bg-primary hover:bg-cyan-400 text-black"
                        >
                            View Upcoming Tournaments
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-8">
                        {Object.entries(matchesByTournament).map(([tournamentId, tournament]) => (
                            <div key={tournamentId} className="space-y-4">
                                {/* Tournament Header */}
                                <div className="flex items-center gap-3 pb-2 border-b border-white/10">
                                    <Trophy className="w-5 h-5 text-cyan-400" />
                                    <h2 className="text-lg font-bold">{tournament.name}</h2>
                                    <span className="text-sm text-muted-foreground">
                                        {tournament.sport}
                                    </span>
                                    <span className="ml-auto text-sm text-red-400 flex items-center gap-1">
                                        <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                                        {tournament.matches.length} live
                                    </span>
                                </div>

                                {/* Match Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {tournament.matches.map(match => (
                                        <LiveMatchCard
                                            key={match.match_id}
                                            match={match}
                                            isStreaming={streamingMatchIds.has(match.match_id)}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// Sport-specific score display helpers
const TENNIS_SPORTS = ['Tennis', 'Padel', 'Squash', 'Badminton', 'Table Tennis'];

const formatTennisScore = (match) => {
    // Parse tennis score from match data if available
    if (match.tennis_data) {
        return match.tennis_data;
    }
    // Fallback to regular score display
    return null;
};

// Live Match Card Component
const LiveMatchCard = ({ match, isStreaming }) => {
    const [expanded, setExpanded] = useState(false);
    const isTennisLike = TENNIS_SPORTS.includes(match.sport);

    return (
        <div className="bg-gradient-to-br from-[#0d2847] to-[#1a365d] border border-white/10 overflow-hidden">
            {/* Live Badge */}
            <div className="bg-red-500 px-3 py-1 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    <span className="text-white text-xs font-bold uppercase">LIVE</span>
                    {isStreaming && (
                        <Link to={`/live-watch/${match.match_id}`} className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded-full text-[10px] font-bold uppercase tracking-wide" data-testid={`watch-stream-${match.match_id}`}>
                            <Tv className="w-3 h-3" /> Watch Stream
                        </Link>
                    )}
                </div>
                <span className="text-white/80 text-xs">
                    {match.current_period || 'In Progress'}
                </span>
            </div>

            {/* Score Display */}
            <div className="p-4">
                {isTennisLike ? (
                    // Tennis-style score display
                    <div className="space-y-2">
                        {/* Header row */}
                        <div className="grid grid-cols-5 gap-2 text-center text-xs text-muted-foreground">
                            <div className="col-span-2"></div>
                            <div>Sets</div>
                            <div>Games</div>
                            <div>Pts</div>
                        </div>
                        {/* Home player */}
                        <div className="grid grid-cols-5 gap-2 items-center">
                            <div className="col-span-2 font-bold text-left truncate">{match.home_team}</div>
                            <div className="text-2xl font-bold text-cyan-400">{match.home_sets || 0}</div>
                            <div className="text-xl font-bold text-yellow-400">{match.home_games || 0}</div>
                            <div className="text-lg font-bold bg-green-600/50 rounded px-2">{match.home_points || '0'}</div>
                        </div>
                        {/* Away player */}
                        <div className="grid grid-cols-5 gap-2 items-center">
                            <div className="col-span-2 font-bold text-left truncate">{match.away_team}</div>
                            <div className="text-2xl font-bold text-cyan-400">{match.away_sets || 0}</div>
                            <div className="text-xl font-bold text-yellow-400">{match.away_games || 0}</div>
                            <div className="text-lg font-bold bg-blue-600/50 rounded px-2">{match.away_points || '0'}</div>
                        </div>
                        {/* Score info */}
                        <div className="text-center text-xs text-muted-foreground pt-2">
                            {match.sport} • Current: {match.current_period || 'Set 1'}
                        </div>
                    </div>
                ) : (
                    // Standard score display
                    <div className="flex items-center justify-between">
                        {/* Home Team */}
                        <div className="flex-1 text-center">
                            <div className="font-bold text-lg mb-1">{match.home_team}</div>
                            <div className="text-5xl font-bold text-white">{match.home_score}</div>
                        </div>

                        {/* VS */}
                        <div className="px-4">
                            <Zap className="w-8 h-8 text-yellow-400" />
                        </div>

                        {/* Away Team */}
                        <div className="flex-1 text-center">
                            <div className="font-bold text-lg mb-1">{match.away_team}</div>
                            <div className="text-5xl font-bold text-white">{match.away_score}</div>
                        </div>
                    </div>
                )}

                {/* Match Info */}
                <div className="flex items-center justify-center gap-4 mt-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                        <Trophy className="w-4 h-4" />
                        {match.round}
                    </span>
                    {match.venue && (
                        <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {match.venue}
                        </span>
                    )}
                </div>

                {/* Recent Events */}
                {match.recent_events && match.recent_events.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                        <button
                            onClick={() => setExpanded(!expanded)}
                            className="w-full flex items-center justify-between text-sm text-muted-foreground hover:text-white transition-colors"
                        >
                            <span>Recent Events ({match.recent_events.length})</span>
                            <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                        </button>
                        
                        {expanded && (
                            <div className="mt-3 space-y-2">
                                {match.recent_events.map((event, idx) => (
                                    <div 
                                        key={idx}
                                        className="flex items-center gap-2 text-sm p-2 bg-black/20"
                                    >
                                        <span className={`w-2 h-2 rounded-full ${event.team === 'home' ? 'bg-green-500' : 'bg-blue-500'}`} />
                                        <span className="flex-1">{event.description || `${event.event_type} - ${event.team_name || event.team}`}</span>
                                        <span className="text-muted-foreground text-xs">{event.period}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LiveScoresPage;
